"use client";

import * as React from "react";
import { CheckIcon } from "lucide-react";
import * as RPNInput from "react-phone-number-input";
import enLabels from "react-phone-number-input/locale/en";
import ruLabels from "react-phone-number-input/locale/ru";
import flags from "react-phone-number-input/flags";
import { useLocale, useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/src/lib/utils";
import { getBrandKeyFromEnv } from "@/lib/branding";

// Default country for the phone input. The examru brand uses the Russian
// standard (+7 RU); every other brand falls back to KZ.
const getDefaultPhoneCountry = (): RPNInput.Country =>
  getBrandKeyFromEnv() === "examru" ? "RU" : "KZ";

type PhoneInputProps = Omit<
  React.ComponentProps<"input">,
  "onChange" | "value" | "ref"
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange?: (value: RPNInput.Value) => void;
  };

const normalizeToE164 = (raw: string | undefined): string | undefined => {
  if (!raw) return undefined;
  // Простейший пример, для Казахстана/России: +7 + 10 цифр
  const digits = raw.replace(/\D/g, "");
  if (!digits) return undefined;
  if (digits.startsWith("8") && digits.length === 11) {
    return `+7${digits.slice(1)}`;
  }
  if (digits.length === 11 && digits.startsWith("7")) {
    return `+${digits}`;
  }
  // Либо более умный парсер через libphonenumber, если подключён
  return `+${digits}`;
};

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
  React.forwardRef<React.ElementRef<typeof RPNInput.default>, PhoneInputProps>(
    ({ className, onChange, value, defaultCountry, ...props }, ref) => {
      const locale = useLocale();
      const resolvedDefaultCountry = defaultCountry ?? getDefaultPhoneCountry();

      // Страна живёт во внутреннем состоянии react-phone-number-input и
      // сбрасывается, когда значение стёрли целиком (Cmd+A → Delete): поле
      // остаётся без флага и без маски-плейсхолдера, будто сломалось.
      // Запоминаем выбор и возвращаем его как defaultCountry.
      const [selectedCountry, setSelectedCountry] =
        React.useState<RPNInput.Country>(resolvedDefaultCountry);

      React.useEffect(() => {
        setSelectedCountry(resolvedDefaultCountry);
      }, [resolvedDefaultCountry]);
      const labels = React.useMemo(() => {
        switch (locale) {
          case "ru":
            return ruLabels;
          case "en":
            return enLabels;
          case "kk":
          default:
            return ruLabels;
        }
      }, [locale]);

      return (
        <RPNInput.default
          ref={ref}
          className={cn(
            // Match design: single white pill with border + shadow (not transparent)
            "flex h-12 overflow-hidden rounded-xl border border-input bg-white shadow-sm",
            className,
          )}
          labels={labels}
          flagComponent={FlagComponent}
          countrySelectComponent={CountrySelect}
          inputComponent={InputComponent}
          smartCaret={false}
          defaultCountry={selectedCountry}
          onCountryChange={(country) => {
            // undefined приходит при очистке — тогда держимся за прошлый выбор
            if (country) setSelectedCountry(country);
          }}
          value={normalizeToE164(value) || undefined}
          /**
           * Handles the onChange event.
           *
           * react-phone-number-input might trigger the onChange event as undefined
           * when a valid phone number is not entered. To prevent this,
           * the value is coerced to an empty string.
           *
           * @param {E164Number | undefined} value - The entered value
           */
          onChange={(value) => onChange?.(value || ("" as RPNInput.Value))}
          {...props}
        />
      );
    },
  );
PhoneInput.displayName = "PhoneInput";

type PhoneInputInnerProps = React.ComponentProps<"input"> & {
  country?: RPNInput.Country;
};

const PLUS7_MASK = "+7 (___) ___ __ __";
const MAX_PLUS7_USER_DIGITS = 10;

function isKzOrRu(country: RPNInput.Country | undefined): boolean {
  return country === "KZ" || country === "RU";
}

function extractPlus7UserDigits(rawValue: string): string {
  const trimmed = rawValue.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";

  // If the string already contains "+7", the first digit is the country code and must be ignored.
  // This is critical for partial input like "+77" (meaning +7 + user typed "7").
  if (trimmed.startsWith("+7")) {
    return digits.slice(1).slice(0, MAX_PLUS7_USER_DIGITS);
  }

  // If user pasted/typed full 11-digit value starting with 7/8 (country/trunk), drop the leading digit.
  if (digits.length >= 11 && (digits.startsWith("7") || digits.startsWith("8"))) {
    return digits.slice(1).slice(0, MAX_PLUS7_USER_DIGITS);
  }

  // Otherwise treat it as a national number (up to 10 digits).
  return digits.slice(0, MAX_PLUS7_USER_DIGITS);
}

function formatPlus7MaskedFromUserDigits(userDigits: string): string {
  if (!userDigits) return "";

  const a = userDigits.slice(0, 3);
  const b = userDigits.slice(3, 6);
  const c = userDigits.slice(6, 8);
  const d = userDigits.slice(8, 10);

  let out = "+7";

  out += ` (${a}`;
  if (a.length === 3) out += ")";

  if (b) out += ` ${b}`;
  if (c) out += ` ${c}`;
  if (d) out += ` ${d}`;

  return out;
}

const getPhonePlaceholderByCountry = (
  country: RPNInput.Country | undefined,
  fallback?: string,
) => {
  // Без страны поле осталось бы вообще без подсказки — берём страну по бренду,
  // чтобы код всегда был виден.
  const resolved = country ?? getDefaultPhoneCountry();
  if (!resolved) return fallback;

  const code = RPNInput.getCountryCallingCode(resolved);

  // Only KZ/RU use this strict visual mask.
  if (code === "7" && isKzOrRu(resolved)) return PLUS7_MASK;

  // Generic fallback for other countries.
  return `+${code}`;
};

function getPlus7UserDigitsCount(rawValue: string): number {
  return extractPlus7UserDigits(rawValue).length;
}

function clampPlus7InputToMaxDigits(rawValue: string): string {
  const userDigits = extractPlus7UserDigits(rawValue);
  if (!userDigits) return "";
  return `+7${userDigits}`;
}

const InputComponent = React.forwardRef<HTMLInputElement, PhoneInputInnerProps>(
  (
    { className, country, placeholder, onKeyDown, onChange, value, ...props },
    ref,
  ) => {
    const rawValue = typeof value === "string" ? value : "";

    // Treat "+7 region" as strict-mask always.
    // This covers cases where `country` prop may be undefined, but user selected KZ/RU in the flag picker.
    //
    // Страна становится undefined после полной очистки поля. Без фолбэка маска
    // отключалась, и следующий ввод «99» уходил в международный режим как +99
    // вместо +7 (99 — при том что плейсхолдер обещал +7.
    const resolvedCountry = country ?? getDefaultPhoneCountry();
    const trimmedValue = rawValue.trim();

    // Явно набранный код другой страны выключает маску: иначе иностранный номер
    // было бы не ввести с клавиатуры, только через выпадающий список стран.
    const isExplicitlyForeign =
      trimmedValue.startsWith("+") && !trimmedValue.startsWith("+7");

    const isPlus7Region =
      !isExplicitlyForeign &&
      (trimmedValue.startsWith("+7") ||
        RPNInput.getCountryCallingCode(resolvedCountry) === "7");

    const displayValue = isPlus7Region
      ? formatPlus7MaskedFromUserDigits(extractPlus7UserDigits(rawValue))
      : rawValue;

    return (
      <Input
        className={cn(
          // Wrapper provides bg/border/radius. Input stays seamless inside.
          "h-12 flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0",
          className,
        )}
        placeholder={
          isPlus7Region ? PLUS7_MASK : getPhonePlaceholderByCountry(country, placeholder)
        }
        {...props}
        ref={ref}
        value={displayValue}
        onKeyDown={(e) => {
          if (isPlus7Region) {
            const key = e.key;
            const isControlAction = e.ctrlKey || e.metaKey || e.altKey;
            const isDigit = /^[0-9]$/.test(key);

            if (!isControlAction && (key === "Backspace" || key === "Delete")) {
              const input = e.currentTarget;
              const cursorStart = input.selectionStart ?? 0;
              const cursorEnd = input.selectionEnd ?? 0;
              const display = input.value;

              let newDisplay: string;

              if (cursorStart !== cursorEnd) {
                // Selection present: remove selected range
                newDisplay = display.slice(0, cursorStart) + display.slice(cursorEnd);
              } else if (key === "Backspace") {
                // Find last digit before cursor and remove it
                let idx = cursorStart - 1;
                while (idx >= 0 && !/[0-9]/.test(display[idx])) idx--;
                if (idx < 0) { e.preventDefault(); return; }
                newDisplay = display.slice(0, idx) + display.slice(idx + 1);
              } else {
                // Delete: find first digit at or after cursor and remove it
                let idx = cursorStart;
                while (idx < display.length && !/[0-9]/.test(display[idx])) idx++;
                if (idx >= display.length) { e.preventDefault(); return; }
                newDisplay = display.slice(0, idx) + display.slice(idx + 1);
              }

              (e.target as HTMLInputElement).value = clampPlus7InputToMaxDigits(newDisplay);
              onChange?.(e as unknown as React.ChangeEvent<HTMLInputElement>);
              e.preventDefault();
              return;
            }

            // «+» в пустом поле — заявка на иностранный номер, её пропускаем.
            // Остальные не-цифры маска не принимает.
            const startsForeignInput =
              key === "+" && e.currentTarget.value.trim().length === 0;

            if (!isControlAction && key.length === 1 && !isDigit && !startsForeignInput) {
              e.preventDefault();
              return;
            }

            if (isDigit && !isControlAction) {
              const input = e.currentTarget;
              const selectionStart = input.selectionStart ?? 0;
              const selectionEnd = input.selectionEnd ?? 0;

              // Allow replacing a selection even when at max length.
              const hasSelection = selectionStart !== selectionEnd;

              if (!hasSelection) {
                const currentCount = getPlus7UserDigitsCount(input.value);
                if (currentCount >= MAX_PLUS7_USER_DIGITS) {
                  e.preventDefault();
                  return;
                }
              }
            }
          }

          onKeyDown?.(e);
        }}
        onChange={(e) => {
          // Смотрим на новое значение, а не на пришедшее пропсом: иначе первый
          // же «+» будет приведён к маске +7 и иностранный номер не начать.
          const next = e.target.value.trim();
          const nextIsForeign = next.startsWith("+") && !next.startsWith("+7");

          if (isPlus7Region && !nextIsForeign) {
            e.target.value = clampPlus7InputToMaxDigits(e.target.value);
          }

          onChange?.(e);
        }}
      />
    );
  },
);
InputComponent.displayName = "InputComponent";

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country: RPNInput.Country) => void;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const t = useTranslations();
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);

  const filtered = React.useMemo(() => {
    const term = searchValue.toLowerCase();

    return countryList.filter(({ value, label }) => {
      if (!value) return false;

      const name = label.toLowerCase();
      const code = RPNInput.getCountryCallingCode(value);

      return (
        name.includes(term) ||
        code.includes(term)
      );
    });
  }, [searchValue, countryList]);


  return (
    <Popover
      open={isOpen}
      modal
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) setSearchValue("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="flex h-12 gap-1 rounded-none border-0 border-r border-input bg-transparent px-3 shadow-none focus:z-10 hover:bg-transparent"
          disabled={disabled}
        >
          {/* При полной очистке поля страна становится undefined и кнопка
              остаётся пустым серым квадратом. Показываем страну по бренду. */}
          <FlagComponent
            country={selectedCountry ?? getDefaultPhoneCountry()}
            countryName={selectedCountry ?? getDefaultPhoneCountry()}
          />
          {/* <ChevronsUpDown
            className={cn(
              "-mr-2 size-4 opacity-50",
              disabled ? "hidden" : "opacity-100",
            )}
          /> */}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput
            value={searchValue}
            onValueChange={(value) => {
              setSearchValue(value);

              setTimeout(() => {
                if (scrollAreaRef.current) {
                  const viewportElement = scrollAreaRef.current.querySelector(
                    "[data-radix-scroll-area-viewport]"
                  );
                  if (viewportElement) viewportElement.scrollTop = 0;
                }
              }, 0);
            }}
            placeholder={t("placeholder-country-search")}
          />

          <CommandList>
            <ScrollArea ref={scrollAreaRef} className="h-72">
              <CommandEmpty>{t('label-no-country')}</CommandEmpty>
              <CommandGroup>
                {filtered.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={selectedCountry}
                      onChange={onChange}
                      onSelectComplete={() => setIsOpen(false)}
                    />
                  ) : null,
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};


interface CountrySelectOptionProps extends RPNInput.FlagProps {
  selectedCountry: RPNInput.Country;
  onChange: (country: RPNInput.Country) => void;
  onSelectComplete: () => void;
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}: CountrySelectOptionProps) => {
  const handleSelect = () => {
    onChange(country);
    onSelectComplete();
  };

  return (
    <CommandItem className="gap-2" onSelect={handleSelect}>
      <FlagComponent country={country} countryName={countryName} />

      <span className="flex-1 text-sm">{countryName}</span>

      <span className="text-sm text-foreground/50">
        +{RPNInput.getCountryCallingCode(country)}
      </span>

      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? "opacity-100" : "opacity-0"
          }`}
      />
    </CommandItem>
  );
};


const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20 [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

export { PhoneInput };
